import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { VideoInsight } from "@/api/studio/insights/types/insight.type";
import { Benchmark } from "@/api/studio/library/types/benchmark.type";
import { getBenchmarkComparison } from "@/api/studio/library/utils/get-benchmark-comparison";
import { Progress } from "@/components/ui/progress";
import { VideoBenchmarkPill } from "@/features/reports/components/video-benchmark-pill";
import { VideoMetricRow } from "@/features/reports/components/video-metric-row";
import { cn } from "@/utils/classes";

/* Prop Types */
interface VideoMetricsDetailsProps {
  currentVideo: VideoInsight;
  benchmark?: Benchmark;
}

/**
 * VideoMetricsDetails component
 *
 * Renders the video in-depth right panel: the overall Attention score as a progress bar +
 * percentage with its benchmark comparison, the applied benchmark, and the Hook / Visual ease /
 * Hold sub-metrics as coloured band rows.
 */
export const VideoMetricsDetails = ({ currentVideo, benchmark }: VideoMetricsDetailsProps) => {
  /* Consts */
  const { attention } = currentVideo.creative;
  const comparison = benchmark ? getBenchmarkComparison(attention.score, benchmark) : undefined;
  const qualityScores = [
    { label: "Hook", score: attention.hook.score },
    { label: "Visual ease", score: attention.visualEase.score },
    { label: "Hold", score: attention.hold.score },
  ];

  /* Render */
  return (
    <div className="flex flex-col gap-4 w-[443px] shrink-0">
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <h2 className="font-semibold">Attention</h2>
              <InformationCircleIcon className="size-4 text-base-content/50" />
            </div>
            <p className="text-sm text-base-content/70 max-w-[236px]">The overall Attention score of the video.</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <h2 className="font-semibold">{Math.round(attention.score)}%</h2>
            {comparison && <VideoBenchmarkPill comparison={comparison} />}
          </div>
        </div>
        <div className="relative">
          <Progress value={Math.round(attention.score)} className="w-full h-2" />
          {benchmark && (
            <div
              data-testid="benchmark-range-marker"
              className="absolute -top-[3px] -bottom-[3px] rounded-full border-2 border-dashed border-base-content/40"
              style={{ left: `${benchmark.scoreRangeMin}%`, width: `${benchmark.scoreRangeMax - benchmark.scoreRangeMin}%` }}
            />
          )}
        </div>

        {benchmark && (
          <div className="flex items-center justify-between bg-base-200 rounded-lg p-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <p className="font-semibold">{benchmark.name}</p>
                <InformationCircleIcon className="size-4 text-base-content/50" />
              </div>
              <p className="text-sm text-base-content/70">{benchmark.videoCount.toLocaleString()} videos</p>
            </div>
            <p className="font-semibold">
              {benchmark.scoreRangeMin}-{benchmark.scoreRangeMax}%
            </p>
          </div>
        )}
      </div>

      <h2 className="font-semibold">Quality scores</h2>

      <div className="flex flex-col rounded-lg overflow-hidden">
        {qualityScores.map(({ label, score }, index) => (
          <div key={label} className={cn("bg-base-200 px-4 py-3", index < qualityScores.length - 1 && "border-b border-base-100")}>
            <VideoMetricRow label={label} score={score} />
          </div>
        ))}
      </div>
    </div>
  );
};
